import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOpenAiStatus } from '@/hooks/useOpenAiStatus';
import { superAiApi } from '../api';
import type {
  AIConversation,
  AIMessage,
  AIDraft,
  ScopeSnapshot,
  RecipientItem,
  AccessTier,
} from '../types';
import {
  CONTEXT_FILTERS,
  QUICK_ACTIONS,
  SCOPE_LABELS,
  CANONICAL_COLLECTIONS,
} from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  activeRole: string;
  initialPrompt?: string;
  initialContextMode?: string;
}

const scopeBadgeClass: Record<AccessTier, string> = {
  GLOBAL: 'text-amber-400 border-amber-400/40 bg-amber-400/10',
  DEPARTMENT: 'text-cyan-400 border-cyan-400/40 bg-cyan-400/10',
  ASSIGNED: 'text-emerald-400 border-emerald-400/40 bg-emerald-400/10',
  PERSONAL: 'text-violet-400 border-violet-400/40 bg-violet-400/10',
};

const sourceStatusColor: Record<string, string> = {
  Актуально: 'text-emerald-400',
  'Требует проверки': 'text-amber-400',
  'Источник устарел': 'text-rose-400',
  'Источник недоступен': 'text-slate-400',
  Проверить: 'text-amber-400',
};

export const SuperAiWorkspaceModal = ({ isOpen, onClose, activeRole, initialPrompt, initialContextMode }: Props) => {
  const navigate = useNavigate();
  const [policy, setPolicy] = useState<ScopeSnapshot | null>(null);
  const [conversation, setConversation] = useState<AIConversation | null>(null);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [draft, setDraft] = useState<AIDraft | null>(null);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [contextMode, setContextMode] = useState(initialContextMode ?? 'auto');


  const [expandedSourceMsgId, setExpandedSourceMsgId] = useState<string | null>(null);
  const [isLeftOpen, setIsLeftOpen] = useState(true);
  const [isRightOpen, setIsRightOpen] = useState(true);

  const [recipientFilter, setRecipientFilter] = useState<Record<string, unknown>>({});
  const [recipients, setRecipients] = useState<RecipientItem[]>([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [recipientPhase, setRecipientPhase] = useState<'idle' | 'selecting' | 'confirmed' | 'conflict'>('idle');
  const [recipientSelectionId, setRecipientSelectionId] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<{ departments: string[]; courses: number[]; programs: string[] }>({ departments: [], courses: [], programs: [] });

  const [showKnowledgeBase, setShowKnowledgeBase] = useState(false);
  const [knowledgeSources, setKnowledgeSources] = useState<import('../types').KnowledgeSource[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const voiceAvailable = typeof window !== 'undefined' && 'webkitSpeechRecognition' in window;
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<unknown>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setIsLoading(true);

    const init = async () => {
      try {
        const pol = await superAiApi.getPolicy(activeRole);
        if (cancelled) return;
        setPolicy(pol);

        const conv = await superAiApi.createConversation('Диалог', 'auto', activeRole);
        if (cancelled) return;
        setConversation(conv);

        setMessages([
          {
            id: 'seed-welcome',
            conversation_id: conv.id,
            actor: 'AI',
            text: `Рабочая зона загружена. Область доступа: ${SCOPE_LABELS[pol.access_tier]}. Я формирую ответы строго в пределах разрешённого контекста. Для write-действий сначала покажу Preview.`,
            source_refs: [],
            draft_id: null,
            created_at: new Date().toISOString(),
          },
        ]);

        if (initialPrompt) {
          setInputText(initialPrompt);
        }
      } catch {
        setMessages([
          {
            id: 'seed-error',
            conversation_id: '',
            actor: 'AI',
            text: 'Ошибка загрузки рабочей зоны. Проверьте соединение и обновите страницу.',
            source_refs: [],
            draft_id: null,
            created_at: new Date().toISOString(),
          },
        ]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void init();
    return () => { cancelled = true; };
  }, [isOpen, activeRole, initialPrompt]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!isOpen) {
      setDraft(null);
      setRecipientPhase('idle');
      setRecipients([]);
      setSelectedUserIds([]);
      setRecipientSelectionId(null);
      setShowKnowledgeBase(false);
    }
  }, [isOpen]);

  const send = useCallback(async () => {
    const text = inputText.trim();
    if (!text || isSending || !conversation) return;

    setInputText('');
    setIsSending(true);
    setDraft(null);
    setRecipientPhase('idle');

    const tempId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), conversation_id: conversation.id, actor: 'USER', text, source_refs: [], draft_id: null, created_at: new Date().toISOString() },
      { id: tempId, conversation_id: conversation.id, actor: 'AI', text: '...', source_refs: [], draft_id: null, created_at: new Date().toISOString() },
    ]);

    try {
      const result = await superAiApi.sendMessage(conversation.id, text, contextMode, selectedFilter ?? undefined);
      setMessages((prev) => {
        const withoutTemp = prev.filter((m) => m.id !== tempId);
        // Keep optimistic user bubble; replace typing placeholder with real AI message.
        const hasUser = withoutTemp.some((m) => m.actor === 'USER' && m.text === text);
        const next = hasUser ? withoutTemp : [...withoutTemp, result.user_message];
        return [...next.filter((m) => !(m.actor === 'AI' && m.text === '...')), result.ai_message];
      });
      if (result.draft) {
        setDraft(result.draft);
      }
    } catch {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId
            ? { ...m, text: 'Ошибка обработки запроса. Повторите попытку.' }
            : m,
        ),
      );
    } finally {
      setIsSending(false);
    }
  }, [inputText, isSending, conversation, contextMode, selectedFilter]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const handleApproveDraft = async () => {
    if (!draft) return;
    try {
      const updated = await superAiApi.approveDraft(draft.id);
      setDraft(updated);
      setRecipientPhase('selecting');
      const resolved = await superAiApi.resolveRecipients({});
      setRecipients(resolved.recipients);
      setRecipientCount(resolved.count);
      setSelectedUserIds(resolved.recipients.map((r) => r.user_id));
      setFilterOptions(resolved.filter_options);
    } catch {
      setDraft((prev) => prev ? { ...prev, status: 'AI_DRAFT' } : prev);
    }
  };

  const handleEditDraft = () => {
    if (!draft) return;
    setDraft((prev) => prev ? { ...prev, status: 'EDITED' } : prev);
  };

  const handleConfirmRecipients = async () => {
    if (!draft) return;
    try {
      const result = await superAiApi.confirmRecipients(draft.id, recipientFilter, selectedUserIds);
      setRecipientSelectionId(result.selection_id);
      setRecipientPhase('confirmed');
    } catch {
      setRecipientPhase('conflict');
    }
  };

  const handleSendDraft = async () => {
    if (!draft || !recipientSelectionId) return;
    try {
      await superAiApi.executeDraft(draft.id, recipientSelectionId);
      setDraft(null);
      setRecipientPhase('idle');
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          conversation_id: conversation?.id ?? '',
          actor: 'AI',
          text: `Действие выполнено успешно. Отправлено ${selectedUserIds.length} получателям.`,
          source_refs: [],
          draft_id: null,
          created_at: new Date().toISOString(),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          conversation_id: conversation?.id ?? '',
          actor: 'AI',
          text: 'Ошибка отправки. Черновик сохранён со статусом APPROVED. Вы можете повторить отправку.',
          source_refs: [],
          draft_id: null,
          created_at: new Date().toISOString(),
        },
      ]);
    }
  };

  const handleVoice = () => {
    if (!voiceAvailable) return;
    if (isListening) {
      (recognitionRef.current as { stop: () => void })?.stop();
      setIsListening(false);
      return;
    }
    const SpeechRecognition = (window as unknown as Record<string, unknown>)['webkitSpeechRecognition'] as new () => {
      continuous: boolean; interimResults: boolean; lang: string;
      onresult: (e: { results: { [i: number]: { [i: number]: { transcript: string } } } }) => void;
      onend: () => void; start: () => void;
    };
    const rec = new SpeechRecognition();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = 'ru-RU';
    rec.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInputText((prev) => prev + transcript);
    };
    rec.onend = () => setIsListening(false);
    recognitionRef.current = rec;
    rec.start();
    setIsListening(true);
  };

  const loadKnowledgeSources = async (collection?: string) => {
    try {
      const sources = await superAiApi.listKnowledgeSources(collection ?? undefined);
      setKnowledgeSources(sources);
    } catch {
      setKnowledgeSources([]);
    }
  };

  const toggleRecipient = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const handleOpenSurveyBuilder = () => {
    if (!draft || draft.action_type !== 'CREATE_SURVEY') return;
    navigate('/app/research-tools?view=create-survey', {
      state: { superAiSurveyDraft: draft.payload },
    });
    onClose();
  };

  const tier = policy?.access_tier ?? 'PERSONAL';
  const isOwner = tier === 'GLOBAL';
  const { showUnconfiguredHint } = useOpenAiStatus(isOpen && isOwner);

  if (!isOpen) return null;

  const filters = CONTEXT_FILTERS[tier];
  const quickActions = QUICK_ACTIONS[tier];
  const scopeLabel = SCOPE_LABELS[tier];

  return (
    <div
      className="fixed inset-0 z-50 bg-[#060d16] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Super AI Workspace"
      id="super-ai-workspace"
    >
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-800/80 bg-[#080f1a] shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-lg font-black text-white tracking-tight">✦ Super AI</span>
          {policy && (
            <span
              className={`hidden sm:inline-flex text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${scopeBadgeClass[tier]}`}
            >
              {scopeLabel}
            </span>
          )}
          {showUnconfiguredHint ? (
            <span className="hidden md:inline text-[11px] text-amber-400/90 font-medium">
              OpenAI не настроен — ответы из базы знаний / RAG
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="sm:hidden p-2 rounded-lg hover:bg-slate-800 text-slate-400 text-xs"
            onClick={() => setIsLeftOpen((v) => !v)}
            aria-label="Контекст"
          >
            ☰
          </button>
          <button
            type="button"
            className="sm:hidden p-2 rounded-lg hover:bg-slate-800 text-slate-400 text-xs"
            onClick={() => setIsRightOpen((v) => !v)}
            aria-label="Источники"
          >
            ⊞
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 hover:text-white text-sm font-semibold transition"
          >
            ✕
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {isLeftOpen && (
          <aside className="w-52 shrink-0 flex flex-col border-r border-slate-800/60 bg-[#080f1a] overflow-y-auto">
            <div className="px-3 pt-4 pb-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Контекст</span>
            </div>
            <div className="flex flex-col gap-1 px-2 pb-4">
              {filters.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setSelectedFilter(selectedFilter === filter ? null : filter)}
                  className={`text-left text-xs px-3 py-2 rounded-lg transition font-medium ${
                    selectedFilter === filter
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
            <div className="mt-auto px-3 pb-4">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                Режим
              </label>
              <select
                value={contextMode}
                onChange={(e) => setContextMode(e.target.value)}
                className="w-full text-xs bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-slate-300 focus:outline-none focus:border-cyan-500"
              >
                <option value="auto">Автоматически</option>
                <option value="my_data">Только мои данные</option>
                <option value="documents">Только документы</option>
                <option value="regulatory">Нормативная база</option>
                <option value="journals">Журналы</option>
                <option value="internet">Интернет + внутренние</option>
                {isOwner && <option value="full_system">Вся система</option>}
              </select>
            </div>
          </aside>
        )}

        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
          <div className="shrink-0 px-4 py-2 border-b border-slate-800/40 flex flex-wrap gap-1.5">
            {quickActions.map((action) => (
              <button
                key={action}
                type="button"
                onClick={() => setInputText(action + ': ')}
                className="px-3 py-1 text-xs font-semibold rounded-full border border-slate-700 bg-slate-900/60 text-slate-300 hover:border-cyan-500/60 hover:text-cyan-300 transition"
              >
                {action}
              </button>
            ))}
            {isOwner && (
              <button
                type="button"
                className="ml-auto px-3 py-1 text-xs font-semibold rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition"
                onClick={() => { setShowKnowledgeBase(true); void loadKnowledgeSources(); }}
              >
                База знаний
              </button>
            )}
          </div>

          {isLoading && (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
              <span className="animate-pulse">Загрузка рабочей зоны...</span>
            </div>
          )}

          {!isLoading && (
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex flex-col gap-1 ${msg.actor === 'USER' ? 'items-end' : 'items-start'}`}>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {msg.actor === 'USER' ? 'Вы' : msg.actor === 'OWNER' ? 'Владелец системы' : 'Super AI'}
                  </span>
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      msg.actor === 'USER'
                        ? 'bg-cyan-500/20 text-cyan-100 border border-cyan-500/30'
                        : msg.actor === 'OWNER'
                          ? 'bg-amber-500/15 text-amber-100 border border-amber-500/30'
                          : 'bg-slate-800/80 text-slate-200 border border-slate-700/60'
                    }`}
                  >
                    {msg.text}
                  </div>
                  {msg.source_refs.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setExpandedSourceMsgId(expandedSourceMsgId === msg.id ? null : msg.id)}
                      className="text-[11px] text-slate-500 hover:text-slate-300 transition flex items-center gap-1"
                    >
                      Источники {expandedSourceMsgId === msg.id ? '▴' : '▾'}
                    </button>
                  )}
                  {expandedSourceMsgId === msg.id && (
                    <div className="flex flex-col gap-1.5 max-w-[80%] w-full">
                      {msg.source_refs.map((ref, i) => (
                        <div key={i} className="px-3 py-2 rounded-lg bg-slate-900/80 border border-slate-700/60 text-xs space-y-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-slate-300 font-medium">{ref.title}</span>
                            <span className={`font-semibold shrink-0 ${sourceStatusColor[ref.status] ?? 'text-slate-400'}`}>
                              {ref.status}
                            </span>
                          </div>
                          {ref.version_no ? (
                            <div className="text-cyan-400/90">Версия: {ref.version_no}</div>
                          ) : null}
                          {ref.citation_anchor ? (
                            <div className="text-slate-500">Фрагмент: {ref.citation_anchor}</div>
                          ) : null}
                          {ref.excerpt_text ? (
                            <div className="text-slate-500 line-clamp-2">{ref.excerpt_text}</div>
                          ) : null}
                          {ref.url ? (
                            <button
                              type="button"
                              className="text-cyan-400 hover:text-cyan-300"
                              onClick={() => navigate(ref.url!)}
                            >
                              Открыть в File Hub
                            </button>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {draft && (
                <div className="border border-cyan-500/30 rounded-2xl bg-[#0b1928] overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-800/60">
                    <span className="text-[10px] font-black uppercase tracking-widest text-cyan-400">Готовый объект</span>
                    <h4 className="text-white font-bold mt-0.5">{String(draft.payload['title'] ?? draft.action_type)}</h4>
                  </div>
                  <div className="px-5 py-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    {Object.entries(draft.payload).map(([k, v]) =>
                      k !== 'questions' ? (
                        <div key={k} className="contents">
                          <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}</span>
                          <span className="text-slate-200 font-medium">{String(v)}</span>
                        </div>
                      ) : null,
                    )}
                    <div className="contents">
                      <span className="text-slate-500">Состояние</span>
                      <span className={`font-black uppercase tracking-widest ${draft.status === 'APPROVED' ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {draft.status}
                      </span>
                    </div>
                  </div>
                  <div className="px-5 py-4 flex flex-wrap gap-2 border-t border-slate-800/60">
                    {draft.action_type === 'CREATE_SURVEY' && (
                      <button
                        type="button"
                        onClick={handleOpenSurveyBuilder}
                        className="px-4 py-2 rounded-xl border border-cyan-500/50 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 text-sm font-semibold transition"
                      >
                        Открыть конструктор опроса
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleEditDraft}
                      className="px-4 py-2 rounded-xl border border-violet-500/50 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 text-sm font-semibold transition"
                    >
                      Изменить
                    </button>
                    {draft.status !== 'APPROVED' && (
                      <button
                        type="button"
                        onClick={() => void handleApproveDraft()}
                        className="px-4 py-2 rounded-xl border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 text-sm font-semibold transition"
                      >
                        Утвердить
                      </button>
                    )}
                    {draft.status === 'APPROVED' && (
                      <span className="px-3 py-2 rounded-xl bg-emerald-500/15 text-emerald-400 text-sm font-black border border-emerald-500/30">
                        ✓ Утверждено
                      </span>
                    )}
                  </div>
                  {draft.status !== 'APPROVED' && (
                    <p className="text-[11px] text-rose-400 px-5 pb-4">
                      Отправка недоступна до APPROVED. После утверждения откроется динамический выбор получателей.
                    </p>
                  )}
                </div>
              )}

              {recipientPhase === 'selecting' && (
                <div className="border border-slate-700/60 rounded-2xl bg-[#0b1928] overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-800/60 flex items-center justify-between">
                    <h4 className="text-white font-bold">Выбор получателей</h4>
                    <span className="text-sm text-cyan-400 font-semibold">Найдено: {recipientCount} чел.</span>
                  </div>
                  <div className="px-5 py-3 flex flex-wrap gap-2">
                    <select
                      onChange={(e) => {
                        const dept = e.target.value;
                        setRecipientFilter((prev) => ({ ...prev, department: dept || undefined }));
                        void superAiApi.resolveRecipients({ department: dept || undefined }).then((r) => {
                          setRecipients(r.recipients);
                          setRecipientCount(r.count);
                          setSelectedUserIds(r.recipients.map((x) => x.user_id));
                        });
                      }}
                      className="text-xs bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-slate-300 focus:outline-none focus:border-cyan-500"
                    >
                      <option value="">Все подразделения</option>
                      {filterOptions.departments.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select
                      onChange={(e) => {
                        const year = e.target.value ? parseInt(e.target.value) : undefined;
                        setRecipientFilter((prev) => ({ ...prev, course_year: year }));
                        void superAiApi.resolveRecipients({ course_year: year }).then((r) => {
                          setRecipients(r.recipients);
                          setRecipientCount(r.count);
                          setSelectedUserIds(r.recipients.map((x) => x.user_id));
                        });
                      }}
                      className="text-xs bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-slate-300 focus:outline-none focus:border-cyan-500"
                    >
                      <option value="">Все курсы</option>
                      {filterOptions.courses.map((c) => <option key={c} value={c}>{c} курс</option>)}
                    </select>
                  </div>
                  <div className="max-h-40 overflow-y-auto px-5 space-y-1 pb-2">
                    {recipients.slice(0, 20).map((r) => (
                      <label key={r.user_id} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer py-1">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(r.user_id)}
                          onChange={() => toggleRecipient(r.user_id)}
                          className="rounded"
                        />
                        <span className="font-medium">{r.full_name}</span>
                        <span className="text-slate-500">{r.role}</span>
                      </label>
                    ))}
                  </div>
                  <div className="px-5 py-4 border-t border-slate-800/60 flex items-center justify-between">
                    <span className="text-xs text-slate-400">Выбрано: <span className="text-white font-bold">{selectedUserIds.length}</span></span>
                    <button
                      type="button"
                      disabled={selectedUserIds.length === 0}
                      onClick={() => void handleConfirmRecipients()}
                      className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-sm font-black transition"
                    >
                      Подтвердить аудиторию
                    </button>
                  </div>
                </div>
              )}

              {recipientPhase === 'conflict' && (
                <div className="border border-rose-500/40 rounded-2xl bg-rose-500/10 px-5 py-4 text-sm text-rose-300">
                  Состав получателей изменился после предпросмотра. Пересчёт аудитории и повторное подтверждение обязательны.
                  <button type="button" onClick={() => setRecipientPhase('selecting')} className="ml-3 underline text-rose-400 hover:text-rose-200">
                    Пересчитать
                  </button>
                </div>
              )}

              {recipientPhase === 'confirmed' && recipientSelectionId && (
                <div className="border border-emerald-500/30 rounded-2xl bg-emerald-500/10 px-5 py-4">
                  <p className="text-sm text-emerald-300 font-semibold mb-3">
                    Аудитория подтверждена: {selectedUserIds.length} получателей
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleSendDraft()}
                    className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-black shadow-[0_0_20px_rgba(245,158,11,0.3)] transition"
                  >
                    Отправить
                  </button>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="shrink-0 px-4 pb-4 pt-3 border-t border-slate-800/60 flex items-end gap-2"
          >
            <label className="p-2.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-xl cursor-pointer transition border border-transparent hover:border-slate-700">
              📎
              <input type="file" className="hidden" aria-label="Прикрепить файл" />
            </label>
            <button
              type="button"
              onClick={handleVoice}
              disabled={!voiceAvailable}
              className={`p-2.5 rounded-xl border transition ${
                isListening
                  ? 'border-rose-500 bg-rose-500/20 text-rose-400 animate-pulse'
                  : voiceAvailable
                    ? 'border-transparent hover:border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                    : 'border-transparent text-slate-600 cursor-not-allowed'
              }`}
              title={voiceAvailable ? 'Голосовой ввод' : 'Браузер не поддерживает голосовой ввод'}
              aria-label="Голосовой ввод"
            >
              🎤
            </button>
            <textarea
              ref={textareaRef}
              rows={2}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Введите вопрос или команду..."
              className="flex-1 bg-slate-900 border border-slate-700/80 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500 resize-none transition"
            />
            <button
              type="submit"
              disabled={isSending || !inputText.trim()}
              className="px-5 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-slate-950 font-black text-sm shadow-lg transition"
            >
              ↑
            </button>
          </form>
        </main>

        {isRightOpen && (
          <aside className="w-56 shrink-0 flex flex-col border-l border-slate-800/60 bg-[#080f1a] overflow-y-auto">
            <div className="px-3 pt-4 pb-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Источники</span>
            </div>
            <div className="flex flex-col gap-2 px-3 pb-4">
              {[
                { label: 'Платформа', status: 'Актуально', statusClass: 'text-emerald-400' },
                { label: 'База знаний', status: 'Актуально', statusClass: 'text-emerald-400' },
                { label: 'Scopus / WoS', status: 'Проверить', statusClass: 'text-amber-400' },
                { label: 'Internet', status: 'По запросу', statusClass: 'text-slate-400' },
              ].map((s) => (
                <div key={s.label} className="p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                  <p className="text-xs font-semibold text-slate-200">{s.label}</p>
                  <p className={`text-[11px] font-bold mt-0.5 ${s.statusClass}`}>{s.status}</p>
                </div>
              ))}
              <div className="mt-2 px-1 py-2 rounded-lg bg-slate-900/40 border border-slate-800/60 text-[11px] text-slate-500 leading-relaxed">
                AI формирует ответы только из разрешённого контекста.
                <br />
                Область: <span className={`font-bold ${scopeBadgeClass[tier].split(' ')[0]}`}>{scopeLabel}</span>
              </div>
            </div>
          </aside>
        )}
      </div>

      {showKnowledgeBase && isOwner && (
        <div className="absolute inset-0 bg-[#060d16]/98 flex flex-col z-10">
          <header className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-amber-400">Владелец системы</span>
              <h2 className="text-xl font-black text-white mt-0.5">База знаний Super AI</h2>
            </div>
            <button type="button" onClick={() => setShowKnowledgeBase(false)} className="px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-300 hover:text-white text-sm font-semibold">
              ← Назад
            </button>
          </header>
          <div className="flex flex-1 overflow-hidden">
            <div className="w-52 border-r border-slate-800 flex flex-col gap-1 p-3 overflow-y-auto shrink-0">
              <button
                type="button"
                onClick={() => { setSelectedCollection(null); void loadKnowledgeSources(); }}
                className={`text-left text-xs px-3 py-2 rounded-lg ${!selectedCollection ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'}`}
              >
                Все коллекции
              </button>
              {CANONICAL_COLLECTIONS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { setSelectedCollection(c.id); void loadKnowledgeSources(c.id); }}
                  className={`text-left text-xs px-3 py-2 rounded-lg ${selectedCollection === c.id ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-white">
                  {selectedCollection ? CANONICAL_COLLECTIONS.find((c) => c.id === selectedCollection)?.label : 'Все источники'} · {knowledgeSources.length} записей
                </h3>
                <button
                  type="button"
                  onClick={() => void superAiApi.syncJournals().then(() => void loadKnowledgeSources(selectedCollection ?? undefined))}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25 transition"
                >
                  Синхронизировать журналы
                </button>
              </div>
              <div className="space-y-2">
                {knowledgeSources.map((src) => (
                  <div key={src.id} className="flex items-start justify-between p-4 rounded-xl bg-slate-900/60 border border-slate-800 gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-100 truncate">{src.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{src.collection} · {src.category} · {src.language}</p>
                      <p className="text-xs text-slate-600 mt-1 line-clamp-2">{src.content_summary}</p>
                    </div>
                    <div className="flex flex-col items-end shrink-0 gap-1">
                      <span className={`text-[10px] font-black uppercase ${src.status === 'ACTIVE' ? 'text-emerald-400' : src.status === 'OUTDATED' ? 'text-amber-400' : 'text-rose-400'}`}>
                        {src.status}
                      </span>
                      <span className="text-[10px] text-slate-600">
                        {new Date(src.last_verified_at).toLocaleDateString('ru-RU')}
                      </span>
                    </div>
                  </div>
                ))}
                {knowledgeSources.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-12">Источники не найдены</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
